import React, { useState, useEffect } from 'react';

// -------------------------------------------------------------
// eyez ON YOU: marketing front-door + onboarding + payment
// Sits on top of the existing Mandate Verifier app.
// On signup completion, redirects to /login (existing app).
// -------------------------------------------------------------

// API base. Reads from CRA env var (set in Railway). Empty string = same origin.
// NOTE: reference process.env.REACT_APP_API_URL directly. CRA replaces this exact
// token with the literal value at build time. Do NOT wrap it in a
// `typeof process !== 'undefined'` guard: in the production browser bundle that
// guard evaluates to false and the whole expression collapses to '', which sends
// API calls to the frontend origin instead of the backend.
const API_BASE = process.env.REACT_APP_API_URL || '';

// WhatsApp "Book a demo" link. Tapping the button opens a WhatsApp chat to your
// business number with a prefilled message, so a visitor can book a session by
// chatting. The number is in international format, digits only (27 is the South
// Africa country code), so the local mobile 0798737581 becomes 27798737581.
const WHATSAPP_NUMBER = '27798737581';
const WHATSAPP_MESSAGE = 'Hi Eyez, I would like to book a demo of the mandate verification platform.';
const WHATSAPP_HREF = 'https://wa.me/' + WHATSAPP_NUMBER + '?text=' + encodeURIComponent(WHATSAPP_MESSAGE);

async function api(path, { method = 'GET', body, token } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try { payload = await res.json(); } catch (e) { payload = null; }
  if (!res.ok) {
    let msg;
    const d = payload && payload.detail;
    if (Array.isArray(d)) {
      // FastAPI 422: detail is an array of {loc, msg, type}. Build a readable string.
      msg = d.map((item) => {
        const field = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : '';
        return field ? `${field}: ${item.msg}` : item.msg;
      }).join('; ');
    } else if (typeof d === 'string') {
      msg = d;
    } else if (d && typeof d === 'object' && d.msg) {
      msg = d.msg;
    } else {
      msg = (payload && payload.message) || `Request failed (${res.status})`;
    }
    const err = new Error(msg);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

// Password rules. MUST match backend validate_password_strength:
//   at least 10 chars, at least 3 of {lower, upper, digit, symbol},
//   not on the common-passwords blocklist (the blocklist is enforced
//   server-side; we cover length and character classes here so the user
//   gets immediate feedback instead of a late server rejection).
function checkPasswordStrength(pw) {
  if (!pw || pw.length < 10) {
    return 'Password must be at least 10 characters';
  }
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^a-zA-Z0-9]/.test(pw) ? 1 : 0);
  if (classes < 3) {
    return 'Use at least three of: lowercase, uppercase, digit, symbol';
  }
  return null; // valid
}


const BRAND = {
  cyan: '#1FB6E6',
  cyanDeep: '#0A8FBE',
  ink: '#0B0F14',
  inkSoft: '#1A2230',
  paper: '#FAFBFC',
  paperWarm: '#F4F6F8',
  line: '#E5E9EF',
  muted: '#6B7686',
};

// Pricing pulled from the platform's BYOK SaaS model in the codebase.
// Numbers are illustrative defaults; super-admin can override per company.
const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    tagline: 'For lean operators getting compliant.',
    monthly: 5000,
    perVoice: 0.50,
    perPdf: 0.30,
    seats: 'Up to 3 users',
    highlights: [
      'Voice + PDF mandate verification',
      'Bring-your-own OpenAI & Anthropic keys',
      'POPIA-aligned audit log',
      'Email support',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: 'For scaling collections and payments teams.',
    monthly: 15000,
    perVoice: 0.50,
    perPdf: 0.30,
    seats: 'Up to 15 users',
    highlights: [
      'Everything in Basic',
      'Higher monthly volumes',
      'Multiple custom rulesets',
      'Priority support + Slack channel',
    ],
    featured: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For banks, TPPPs and high-volume agencies.',
    monthly: 50000,
    perVoice: 0.50,
    perPdf: 0.30,
    seats: 'Unlimited',
    highlights: [
      'Everything in Growth',
      'Custom mandate rulesets',
      'Dedicated CSM + 99.9% SLA',
      'On-premise / private cloud option',
    ],
  },
];

const fmtR = (n) =>
  'R' + n.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// -------------------------------------------------------------
// Iris / aperture logo. Built in SVG so it scales clean
// Inspired by the eyez logo: cyan + black concentric arcs around an iris
// -------------------------------------------------------------
function IrisMark({ size = 40, animate = false }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={{ display: 'block', overflow: 'visible' }}
      aria-hidden="true"
    >
      {animate && (
        <defs>
          <style>{`
            @keyframes irisSpin { to { transform: rotate(360deg); } }
            .iris-outer { transform-origin: 50px 50px; animation: irisSpin 28s linear infinite; }
            .iris-inner { transform-origin: 50px 50px; animation: irisSpin 18s linear infinite reverse; }
          `}</style>
        </defs>
      )}
      {/* outer arcs: black */}
      <g className={animate ? 'iris-outer' : ''}>
        <path
          d="M 50 6 A 44 44 0 0 1 94 50"
          fill="none"
          stroke={BRAND.ink}
          strokeWidth="6"
          strokeLinecap="round"
        />
        <path
          d="M 50 94 A 44 44 0 0 1 6 50"
          fill="none"
          stroke={BRAND.ink}
          strokeWidth="6"
          strokeLinecap="round"
        />
      </g>
      {/* mid arcs: cyan */}
      <g className={animate ? 'iris-inner' : ''}>
        <path
          d="M 16 50 A 34 34 0 0 1 84 50"
          fill="none"
          stroke={BRAND.cyan}
          strokeWidth="7"
          strokeLinecap="round"
        />
        <path
          d="M 84 50 A 34 34 0 0 1 16 50"
          fill="none"
          stroke={BRAND.cyan}
          strokeWidth="7"
          strokeLinecap="round"
          opacity="0.45"
        />
      </g>
      {/* iris */}
      <circle cx="50" cy="50" r="20" fill="#fff" stroke={BRAND.ink} strokeWidth="1.5" />
      {/* pupil with quarter-cut for the "shutter" effect from the source logo */}
      <path
        d="M 50 36 A 14 14 0 1 1 50 64 A 14 14 0 0 1 50 36 Z M 50 36 L 50 50 L 64 50 A 14 14 0 0 0 50 36 Z"
        fill={BRAND.ink}
        fillRule="evenodd"
      />
    </svg>
  );
}

// Wordmark
function Wordmark({ size = 28 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <IrisMark size={size} />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
        <span
          style={{
            fontFamily: '"Fraunces", "Cormorant Garamond", Georgia, serif',
            fontWeight: 600,
            fontSize: size * 0.78,
            letterSpacing: '-0.02em',
            color: BRAND.ink,
          }}
        >
          eyez
        </span>
        <span
          style={{
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontSize: size * 0.26,
            letterSpacing: '0.22em',
            color: BRAND.muted,
            marginTop: 2,
          }}
        >
          ON&nbsp;YOU
        </span>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// Step indicator for onboarding
// -------------------------------------------------------------
function Stepper({ current, steps }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>
      {steps.map((s, i) => {
        const active = i === current;
        const done = i < current;
        return (
          <React.Fragment key={s}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                borderRadius: 999,
                background: active ? BRAND.ink : done ? '#E8F6FC' : 'transparent',
                color: active ? '#fff' : done ? BRAND.cyanDeep : BRAND.muted,
                border: active ? `1px solid ${BRAND.ink}` : done ? `1px solid ${BRAND.cyan}` : `1px solid ${BRAND.line}`,
                transition: 'all .2s ease',
                letterSpacing: '0.08em',
              }}
            >
              <span style={{
                width: 14, height: 14, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: active ? BRAND.cyan : done ? BRAND.cyan : BRAND.line,
                color: active || done ? '#fff' : BRAND.muted,
                fontSize: 9, fontWeight: 700,
              }}>
                {done ? '✓' : i + 1}
              </span>
              {s.toUpperCase()}
            </div>
            {i < steps.length - 1 && (
              <span style={{ color: BRAND.line }}>·</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------
// MAIN APP
// -------------------------------------------------------------
export default function EyezMarketing() {
  // 'marketing' | 'onboard' | 'payment' | 'done'
  const [view, setView] = useState('marketing');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [onboardData, setOnboardData] = useState({
    company_name: '',
    company_size: '',
    industry: '',
    full_name: '',
    work_email: '',
    phone: '',
    password: '',
    accept_terms: false,
  });
  // Session token returned by /signup/create. Used to authorise /signup/payment
  const [session, setSession] = useState(null);
  // Once signup completes, we hand the token off to the existing app via localStorage
  // (matches what App.js / Login.js already read).

  // load fonts once
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Inter:wght@400;500;600&display=swap';
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (e) {} };
  }, []);

  const startSignup = (planId) => {
    setSelectedPlan(planId);
    setView('onboard');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const finishSignup = (data, sessionFromApi) => {
    setOnboardData(data);
    if (sessionFromApi) setSession(sessionFromApi);
    // Persist the token so the app picks up the authenticated session. No card is
    // collected at signup: the trial starts immediately and we ask for a card only
    // when the trial ends.
    const token = sessionFromApi && sessionFromApi.access_token;
    if (token) { try { localStorage.setItem('mv_token', token); } catch (e) {} }
    setView('done');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const completeSignup = () => {
    // Persist token so the existing /login app picks up the authenticated session.
    if (session && session.access_token) {
      try {
        localStorage.setItem('mv_token', session.access_token);
      } catch (e) { /* private-mode fallback: token sits in memory only */ }
    }
    setView('done');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        color: BRAND.ink,
        background: BRAND.paper,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      {view === 'marketing' && <Marketing onStart={startSignup} />}
      {view === 'onboard' && (
        <Onboard
          plan={PLANS.find((p) => p.id === selectedPlan)}
          initial={onboardData}
          onBack={() => setView('marketing')}
          onContinue={finishSignup}
        />
      )}
      {view === 'done' && <Done onboard={onboardData} plan={PLANS.find((p) => p.id === selectedPlan)} session={session} onHome={() => setView('marketing')} />}
    </div>
  );
}

// -------------------------------------------------------------
// MARKETING PAGE (eyez ON YOU, Service 2, day/night, branded loader)
// -------------------------------------------------------------
const EY_CSS = `
.ey{
  --ink:#0A1626; --ink-2:#0F2034; --ink-card:#13263D;
  --iris:#1FB6E8; --iris-deep:#0E7CA8; --iris-soft:#7FD6F2;
  --bg-body:#FFFFFF; --bg-alt:#EFF5F8; --surface:#FFFFFF; --surface-2:#EFF5F8;
  --text:#27384A; --heading:#0A1626; --muted:#65788A;
  --line:#E1EAF0; --line-dk:rgba(255,255,255,.10);
  --nav-bg:rgba(255,251,252,.92); --ring-track:#E5E7EB;
  --ok:#16A34A; --warn:#D97706; --bad:#DC2626;
  --fd:'Space Grotesk',system-ui,sans-serif; --fb:'Inter',system-ui,sans-serif; --fm:'JetBrains Mono',ui-monospace,monospace;
  background:var(--bg-body); color:var(--text); min-height:100vh; font-family:var(--fb); line-height:1.65;
}
.ey[data-theme="dark"]{
  --bg-body:#0A1626; --bg-alt:#0F1F33; --surface:#13263D; --surface-2:#0F1F33;
  --text:#C9D7E3; --heading:#F3F8FC; --muted:#94A9BC;
  --line:rgba(255,255,255,.10); --nav-bg:rgba(10,22,38,.92);
  --iris-deep:#34C0EE; --ring-track:rgba(255,255,255,.14);
}
.ey *{box-sizing:border-box}
.ey a{color:inherit;text-decoration:none}
.ey-wrap{max-width:1180px;margin:0 auto;padding:0 24px}
.ey-eyebrow{font-family:var(--fm);font-size:.74rem;letter-spacing:.22em;text-transform:uppercase;color:var(--iris-deep);font-weight:500}
.ey-eyebrow-cyan{color:var(--iris)}
.ey-center{display:block;text-align:center}
.ey-section{padding:clamp(60px,8vw,108px) 0}
.ey-h1{font-family:var(--fd);font-weight:600;line-height:1.04;letter-spacing:-.02em;color:#fff;font-size:clamp(2.3rem,5vw,3.7rem);margin:16px 0 0}
.ey-h2{font-family:var(--fd);font-weight:600;line-height:1.05;letter-spacing:-.02em;color:var(--heading);font-size:clamp(1.8rem,3.5vw,2.6rem);margin-top:12px}
.ey-accent{color:var(--iris)}
.ey-lead{font-size:1.1rem;color:var(--muted);max-width:52ch;margin-top:16px}
.ey-p{margin-top:14px;max-width:50ch;color:var(--text)}
.ey-btn{display:inline-flex;align-items:center;gap:.5em;font-family:var(--fb);font-weight:600;font-size:.95rem;padding:.74em 1.35em;border-radius:11px;border:1px solid transparent;cursor:pointer;transition:transform .15s ease,box-shadow .2s ease,background .2s ease,border-color .2s ease;white-space:nowrap}
.ey-btn:focus-visible{outline:3px solid var(--iris);outline-offset:2px}
.ey-btn-primary{background:var(--iris);color:#06222F;box-shadow:0 6px 18px -6px rgba(31,182,232,.6)}
.ey-btn-primary:hover{transform:translateY(-1px);background:#34C0EE}
.ey-btn-ghost{background:transparent;border-color:var(--line);color:var(--heading)}
.ey-btn-ghost:hover{border-color:var(--iris-deep);color:var(--iris-deep)}
.ey-btn-lg{padding:.92em 1.6em;font-size:1rem}
.ey-nav{position:fixed;top:0;left:0;right:0;z-index:50;border-bottom:1px solid transparent;transition:background .25s ease,box-shadow .25s ease,border-color .25s ease}
.ey-nav-in{display:flex;align-items:center;gap:22px;height:66px}
.ey-logo{display:inline-flex;align-items:center;gap:.5rem;background:none;border:none;cursor:pointer;padding:0}
.ey-word{font-family:var(--fd);font-weight:700;font-size:1.4rem;letter-spacing:-.01em;color:#fff}
.ey-links{display:flex;gap:24px;margin-left:auto}
.ey-links a{font-size:.92rem;font-weight:500;color:#D7E5EE}
.ey-links a:hover{color:#fff}
.ey-actions{display:flex;align-items:center;gap:10px}
.ey-signin{background:none;border:none;cursor:pointer;font-size:.92rem;font-weight:600;color:#EAF3F8;font-family:var(--fb)}
.ey-signin:hover{color:var(--iris-soft)}
.ey-toggle{background:transparent;border:1px solid rgba(255,255,255,.22);border-radius:9px;width:38px;height:38px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;color:#EAF3F8;transition:border-color .2s ease,color .2s ease}
.ey-toggle:hover{border-color:var(--iris);color:var(--iris-soft)}
.ey-toggle:focus-visible{outline:3px solid var(--iris);outline-offset:2px}
.ey-toggle svg{width:18px;height:18px}
.ey-burger{display:none}
.ey-nav.is-solid{background:var(--nav-bg);backdrop-filter:saturate(1.4) blur(10px);-webkit-backdrop-filter:saturate(1.4) blur(10px);border-color:var(--line)}
.ey-nav.is-solid .ey-word{color:var(--heading)}
.ey-nav.is-solid .ey-links a{color:var(--text)}
.ey-nav.is-solid .ey-links a:hover{color:var(--iris-deep)}
.ey-nav.is-solid .ey-signin{color:var(--text)}
.ey-nav.is-solid .ey-toggle{border-color:var(--line);color:var(--heading)}
.ey-hero{position:relative;background:var(--ink);overflow:hidden;padding-top:128px;padding-bottom:clamp(64px,8vw,110px)}
.ey-hero::before{content:"";position:absolute;top:-30%;right:-10%;width:760px;height:760px;border-radius:50%;background:radial-gradient(circle,rgba(31,182,232,.22),transparent 62%);pointer-events:none}
.ey-hero-grid{position:relative;z-index:1;display:grid;grid-template-columns:1.05fr .95fr;gap:52px;align-items:center}
.ey-sub{font-size:1.14rem;color:#B6CADA;max-width:46ch;margin-top:18px}
.ey-cta-row{display:flex;gap:13px;flex-wrap:wrap;margin-top:28px}
.ey-trustline{font-family:var(--fm);font-size:.78rem;color:#8FA6B8;margin-top:18px}
.ey-readout{background:var(--surface);border-radius:18px;padding:20px;color:var(--text);box-shadow:0 30px 70px -30px rgba(0,0,0,.6);position:relative;overflow:hidden}
.ey-scan{position:absolute;left:0;right:0;top:0;height:60px;background:linear-gradient(to bottom,rgba(31,182,232,.16),transparent);animation:eyScan 3.6s cubic-bezier(.6,0,.4,1) infinite;pointer-events:none}
@keyframes eyScan{0%{transform:translateY(-66px)}55%{transform:translateY(340px)}100%{transform:translateY(340px)}}
.ey-ro-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding-bottom:14px;border-bottom:1px solid var(--line)}
.ey-ro-file{font-family:var(--fm);font-size:.86rem;color:var(--heading);font-weight:500;display:flex;align-items:center;gap:8px}
.ey-dot{width:8px;height:8px;border-radius:50%;background:var(--ok)}
.ey-ro-chip{font-family:var(--fm);font-size:.68rem;letter-spacing:.1em;padding:3px 9px;border-radius:6px;background:var(--surface-2);color:var(--iris-deep);font-weight:500}
.ey-ro-summary{display:flex;align-items:center;gap:18px;padding:16px 0}
.ey-ring{position:relative;flex:none}
.ey-ring-num{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:var(--fm);font-weight:600;font-size:1.05rem;color:var(--ok)}
.ey-badge-ok{font-family:var(--fm);font-size:.72rem;letter-spacing:.08em;font-weight:600;padding:5px 12px;border-radius:8px;background:#F0FDF4;color:#15803D;border:1px solid #BBF7D0}
.ey-ro-meta{font-family:var(--fm);font-size:.78rem;color:var(--muted);margin-top:8px}
.ey-ro-fields{display:grid;grid-template-columns:1fr 1fr;gap:8px 18px;margin-top:4px}
.ey-ro-field{display:flex;align-items:center;gap:8px;font-size:.83rem;color:var(--text)}
.ey-tick{color:var(--ok);font-weight:700}
.ey-ro-foot{margin-top:16px;padding-top:13px;border-top:1px solid var(--line);font-family:var(--fm);font-size:.72rem;color:var(--muted)}
.ey-strip{background:var(--ink-2);border-top:1px solid var(--line-dk);border-bottom:1px solid var(--line-dk)}
.ey-strip-in{display:flex;flex-wrap:wrap;gap:14px 34px;justify-content:center;padding:18px 24px}
.ey-strip-in span{font-family:var(--fm);font-size:.78rem;letter-spacing:.06em;color:#A9C0D2;display:flex;align-items:center;gap:10px}
.ey-strip-in span::before{content:"";width:6px;height:6px;border-radius:50%;background:var(--iris)}
.ey-what-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:50px;align-items:center}
.ey-flow{background:var(--surface-2);border:1px solid var(--line);border-radius:16px;padding:24px}
.ey-flow-step{display:flex;align-items:center;gap:14px;padding:12px 0}
.ey-flow-step + .ey-flow-step{border-top:1px solid var(--line)}
.ey-flow-ic{width:38px;height:38px;border-radius:10px;background:var(--surface);border:1px solid var(--line);display:flex;align-items:center;justify-content:center;color:var(--iris-deep);flex:none}
.ey-flow-step b{font-family:var(--fd);font-weight:600;color:var(--heading);font-size:.96rem;display:block}
.ey-flow-step small{display:block;color:var(--muted);font-size:.84rem;font-weight:400}
.ey-flow-note{font-family:var(--fm);font-size:.74rem;color:var(--iris-deep);margin-top:14px;line-height:1.5}
.ey-steps{background:var(--bg-alt)}
.ey-steps-row{display:grid;grid-template-columns:repeat(3,1fr);gap:26px;margin-top:40px}
.ey-step{position:relative;padding-top:14px}
.ey-step::before{content:"";position:absolute;top:0;left:0;width:46px;height:3px;background:var(--iris);border-radius:3px}
.ey-step .no{font-family:var(--fm);font-size:.8rem;color:var(--iris-deep);letter-spacing:.08em;font-weight:500}
.ey-step h3{font-family:var(--fd);font-size:1.18rem;margin:12px 0 8px;color:var(--heading)}
.ey-step p{color:var(--muted);font-size:.95rem}
.ey-feat-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:40px}
.ey-feat{border:1px solid var(--line);border-radius:16px;padding:24px;background:var(--surface);transition:border-color .2s ease,transform .2s ease}
.ey-feat:hover{transform:translateY(-2px)}
.ey-feat-ic{width:40px;height:40px;border-radius:10px;background:var(--surface-2);display:flex;align-items:center;justify-content:center;color:var(--iris-deep);margin-bottom:14px}
.ey-feat h3{font-family:var(--fd);font-size:1.04rem;margin-bottom:6px;color:var(--heading)}
.ey-feat p{color:var(--muted);font-size:.92rem}
.ey-secure{background:var(--ink)}
.ey-secure .ey-h2{color:#fff}
.ey-secure .ey-lead{color:#A9C0D2}
.ey-sec-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-top:42px}
.ey-sec{background:var(--ink-card);border:1px solid var(--line-dk);border-radius:14px;padding:22px}
.ey-sec-ic{width:30px;height:30px;border-radius:8px;background:rgba(31,182,232,.14);display:flex;align-items:center;justify-content:center;margin-bottom:13px}
.ey-sec-ic i{width:9px;height:9px;border-radius:50%;background:var(--iris);display:block}
.ey-sec h3{font-family:var(--fd);color:#fff;font-size:1rem;margin-bottom:6px}
.ey-sec p{color:#9FB4C6;font-size:.88rem}
.ey-audience{background:var(--bg-alt);text-align:center}
.ey-chips{display:flex;flex-wrap:wrap;gap:12px;justify-content:center;margin-top:26px}
.ey-chip{font-family:var(--fm);font-size:.82rem;letter-spacing:.03em;padding:9px 16px;border-radius:999px;background:var(--surface);border:1px solid var(--line);color:var(--text)}
.ey-pricing{text-align:center}
.ey-price-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:42px;text-align:left}
.ey-plan{border:1px solid var(--line);border-radius:18px;padding:28px 26px;display:flex;flex-direction:column;background:var(--surface)}
.ey-plan.is-featured{border-color:var(--iris);box-shadow:0 24px 50px -28px rgba(31,182,232,.5);position:relative}
.ey-tag{font-family:var(--fm);font-size:.66rem;letter-spacing:.14em;text-transform:uppercase;color:var(--iris-deep);font-weight:500}
.ey-plan.is-featured .ey-popular{position:absolute;top:-11px;left:26px;background:var(--iris);color:#06222F;padding:3px 11px;border-radius:7px}
.ey-price{font-family:var(--fd);font-weight:700;font-size:2.1rem;color:var(--heading);margin:12px 0 2px}
.ey-price span{font-family:var(--fm);font-size:.82rem;font-weight:400;color:var(--muted)}
.ey-who{color:var(--muted);font-size:.9rem;min-height:2.6em}
.ey-plan-ul{list-style:none;margin:16px 0 22px;padding:0;display:flex;flex-direction:column;gap:10px}
.ey-plan-ul li{font-size:.92rem;color:var(--text);display:flex;align-items:flex-start;gap:9px}
.ey-plan-ul li::before{content:"";width:7px;height:7px;border-radius:50%;background:var(--iris);margin-top:.5em;flex:none}
.ey-plan .ey-btn{justify-content:center;width:100%;margin-top:auto}
.ey-price-note{font-family:var(--fm);font-size:.76rem;color:var(--muted);margin-top:22px}
.ey-faq{background:var(--bg-alt)}
.ey-faq-list{max-width:760px;margin:36px auto 0}
.ey-qa{background:var(--surface);border:1px solid var(--line);border-radius:12px;margin-bottom:12px;overflow:hidden}
.ey-qa summary{list-style:none;cursor:pointer;padding:18px 22px;font-family:var(--fd);font-weight:600;color:var(--heading);font-size:1.02rem;display:flex;align-items:center;justify-content:space-between;gap:16px}
.ey-qa summary::-webkit-details-marker{display:none}
.ey-qa summary .pl{color:var(--iris-deep);font-weight:400;font-size:1.4rem;line-height:1;transition:transform .2s ease;flex:none}
.ey-qa[open] summary .pl{transform:rotate(45deg)}
.ey-qa-ans{padding:0 22px 20px;color:var(--muted);font-size:.95rem;max-width:64ch}
.ey-ctaband{background:var(--ink);text-align:center}
.ey-ctaband .ey-h2{color:#fff}
.ey-ctaband p{color:#B6CADA;margin:14px auto 0;max-width:48ch}
.ey-ctaband .ey-cta-row{justify-content:center;margin-top:28px}
.ey-foot{background:#06101C;color:#9FB4C6;padding:60px 0 28px}
.ey-foot-top{display:grid;grid-template-columns:1.5fr 1fr 1fr 1fr;gap:30px}
.ey-foot-logo{display:inline-flex;align-items:center;gap:.5rem}
.ey-foot-logo .ey-word{color:#fff}
.ey-foot-blurb{font-size:.9rem;max-width:34ch;margin-top:14px}
.ey-foot h4{font-family:var(--fm);font-size:.72rem;letter-spacing:.14em;text-transform:uppercase;color:#6E8498;margin-bottom:14px;font-weight:500}
.ey-foot ul{list-style:none;padding:0;display:flex;flex-direction:column;gap:10px}
.ey-foot ul a{font-size:.9rem;color:#B9CCDB}
.ey-foot ul a:hover{color:#fff}
.ey-foot-disc{font-size:.8rem;color:#647A8E;max-width:74ch;margin-top:36px;line-height:1.6}
.ey-foot-bottom{border-top:1px solid rgba(255,255,255,.08);margin-top:22px;padding-top:20px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:10px;font-size:.82rem;color:#6E8498}
.ey-foot-bottom .tag{font-family:var(--fm);letter-spacing:.06em}
.ey-loader{position:fixed;inset:0;z-index:200;display:flex;align-items:center;justify-content:center;background:var(--ink);transition:opacity .45s ease}
.ey-loader.out{opacity:0;pointer-events:none}
@media(max-width:920px){
  .ey-hero-grid,.ey-what-grid{grid-template-columns:1fr;gap:38px}
  .ey-sec-grid{grid-template-columns:repeat(2,1fr)}
  .ey-feat-grid{grid-template-columns:repeat(2,1fr)}
  .ey-steps-row,.ey-price-grid{grid-template-columns:1fr}
  .ey-foot-top{grid-template-columns:1fr 1fr}
  .ey-links{display:none}
  .ey-links.open{display:flex;position:absolute;top:66px;left:0;right:0;flex-direction:column;background:var(--ink-2);padding:18px 24px;gap:14px;border-bottom:1px solid var(--line-dk)}
  .ey-burger{display:inline-flex;background:none;border:1px solid rgba(255,255,255,.25);border-radius:9px;width:40px;height:38px;align-items:center;justify-content:center;cursor:pointer}
  .ey-nav.is-solid .ey-burger{border-color:var(--line)}
  .ey-burger span,.ey-burger span::before,.ey-burger span::after{content:"";display:block;width:18px;height:2px;background:#EAF3F8;position:relative;transition:.2s ease}
  .ey-nav.is-solid .ey-burger span,.ey-nav.is-solid .ey-burger span::before,.ey-nav.is-solid .ey-burger span::after{background:var(--heading)}
  .ey-burger span::before{position:absolute;top:-6px}
  .ey-burger span::after{position:absolute;top:6px}
}
@media(max-width:560px){
  .ey-sec-grid,.ey-feat-grid,.ey-foot-top{grid-template-columns:1fr}
  .ey-ro-fields{grid-template-columns:1fr}
}
@media(prefers-reduced-motion:reduce){ .ey *{animation:none !important} }
`;

function Marketing({ onStart }) {
  const initialTheme = () => {
    try { const t = localStorage.getItem('eyez-theme'); if (t) return t; } catch (e) {}
    try { return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'; } catch (e) { return 'light'; }
  };
  const [theme, setTheme] = useState(initialTheme);
  const [navSolid, setNavSolid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [loaderOut, setLoaderOut] = useState(false);

  useEffect(() => {
    try { localStorage.setItem('eyez-theme', theme); } catch (e) {}
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setNavSolid(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    const t1 = setTimeout(() => setLoaderOut(true), 750);
    const t2 = setTimeout(() => setShowLoader(false), 1180);
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(t1); clearTimeout(t2);
    };
  }, []);

  const toggleTheme = () => setTheme((p) => (p === 'dark' ? 'light' : 'dark'));
  const signIn = () => { window.location.href = '/login'; };
  const fmtR = (n) => 'R' + n.toLocaleString('en-US');

  const fields = ['Account holder', 'Bank account no.', 'Branch code', 'Debit amount', 'Frequency', 'Mandate date', 'Authorisation', 'Signature'];

  const moon = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>);
  const sun = (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>);

  const features = [
    { t: 'Field and wording checks', d: 'Every required field and clause, checked against rulesets you control.', i: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>) },
    { t: 'Voice or PDF', d: 'Verify recorded or written mandates. Voice is transcribed, then checked the same way.', i: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><path d="M12 19v3" /></svg>) },
    { t: 'Batches, handled', d: 'Multi-mandate files are detected and processed individually, with a summary across the batch.', i: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>) },
    { t: 'A readable verdict', d: 'Verdict, score, flags, and the raw transcript, all in one view your team can read.', i: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /></svg>) },
    { t: 'Audit-ready records', d: 'A minimised record per mandate for billing and audit, anonymised after the retention window.', i: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>) },
    { t: 'Data-subject requests', d: 'A built-in workflow routes each request to the right Responsible Party.', i: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 11l-3 3-1.5-1.5" /></svg>) },
  ];

  const security = [
    ['Operator, not owner', 'Eyez processes on your instruction. You stay the Responsible Party for the mandate.'],
    ['Bring your own keys', 'The AI runs on your own OpenAI and Anthropic accounts. Those providers do not train on your data.'],
    ['Source files deleted', 'The uploaded mandate file is removed immediately after processing.'],
    ['Short retention', 'Reports are deleted after 7 days, and the record is anonymised at the same point.'],
    ['Encrypted at rest', 'Keys and sensitive data are encrypted (Fernet AES-128).'],
    ['Two-factor sign-in', 'Authenticator-app 2FA, with role-based access for your team.'],
    ['Audit logging', 'Key actions are recorded so you can see who did what, and when.'],
    ['Agreement ready', 'An Operator agreement is ready for your legal team to review.'],
  ];

  const faqs = [
    ['Is the report a compliance certification?', 'No. It is an automated, AI-assisted quality-assurance check, and AI can make mistakes. That is why your reviewer sees every result and keeps the final decision on every mandate.'],
    ['Where does my data go?', 'Mandates are processed with your own AI keys, on your own provider accounts. The uploaded file is deleted immediately after processing, reports are kept for 7 days, and the record is then anonymised.'],
    ['Do you store the signed mandate?', 'No. The original stays with you, as your record. Eyez keeps a minimised QA record, not the mandate itself, and that record is anonymised after the retention window.'],
    ['Is Eyez POPIA compliant?', 'Eyez is built around POPIA and acts as your Operator, processing on your instruction. You remain the Responsible Party for the mandate, and an Operator agreement is available for your legal team.'],
    ['Can it handle voice mandates?', 'Yes. A voice mandate is transcribed and then checked the same way as a written one. Nothing is used to identify a person by their voice.'],
  ];

  return (
    <div className="ey" data-theme={theme}>
      <style>{EY_CSS}</style>

      {showLoader && (
        <div className={'ey-loader' + (loaderOut ? ' out' : '')} aria-hidden="true">
          <IrisMark size={64} animate />
        </div>
      )}

      <header className={'ey-nav' + (navSolid ? ' is-solid' : '')}>
        <div className="ey-wrap ey-nav-in">
          <button className="ey-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Eyez home">
            <IrisMark size={32} />
            <span className="ey-word">eyez</span>
          </button>
          <nav className={'ey-links' + (menuOpen ? ' open' : '')} onClick={() => setMenuOpen(false)} aria-label="Primary">
            <a href="#how">How it works</a>
            <a href="#security">Security</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="ey-actions">
            <button className="ey-toggle" onClick={toggleTheme} aria-label="Switch between day and night" title="Day / night">
              {theme === 'dark' ? sun : moon}
            </button>
            <button className="ey-signin" onClick={signIn}>Sign in</button>
            <button className="ey-btn ey-btn-primary" onClick={() => onStart('growth')}>Start free trial</button>
            <button className="ey-burger" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu"><span /></button>
          </div>
        </div>
      </header>

      <main>
        <section className="ey-hero">
          <div className="ey-wrap ey-hero-grid">
            <div>
              <span className="ey-eyebrow ey-eyebrow-cyan">POPIA-aligned mandate QA</span>
              <h1 className="ey-h1">An eye on every debit-order mandate, <span className="ey-accent">after it is signed.</span></h1>
              <p className="ey-sub">Eyez reads a signed mandate, checks the required fields and wording against your own rules, and returns an audit-ready QA report. Your data, your AI keys, your decision.</p>
              <div className="ey-cta-row">
                <button className="ey-btn ey-btn-primary ey-btn-lg" onClick={() => onStart('growth')}>Start free trial</button>
                <a className="ey-btn ey-btn-ghost ey-btn-lg" href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer">Book a demo</a>
                <a className="ey-btn ey-btn-ghost ey-btn-lg" href="#pricing">See pricing</a>
              </div>
              <p className="ey-trustline">14-day free trial. No mandate file kept after processing.</p>
            </div>
            <div>
              <div className="ey-readout" role="img" aria-label="Example Eyez verification, marked compliant with a score of 96 and all eight fields present.">
                <div className="ey-scan" />
                <div className="ey-ro-head">
                  <span className="ey-ro-file"><span className="ey-dot" />mandate_4471.pdf</span>
                  <span className="ey-ro-chip">PDF</span>
                </div>
                <div className="ey-ro-summary">
                  <span className="ey-ring">
                    <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true">
                      <circle cx="32" cy="32" r="26" fill="none" stroke="var(--ring-track)" strokeWidth="6" />
                      <circle cx="32" cy="32" r="26" fill="none" stroke="#16A34A" strokeWidth="6" strokeLinecap="round" strokeDasharray="157 163" transform="rotate(-90 32 32)" />
                    </svg>
                    <span className="ey-ring-num">96</span>
                  </span>
                  <div>
                    <span className="ey-badge-ok">COMPLIANT</span>
                    <div className="ey-ro-meta">8 / 8 fields &middot; 0 flags</div>
                  </div>
                </div>
                <div className="ey-ro-fields">
                  {fields.map((f) => (<span className="ey-ro-field" key={f}><span className="ey-tick">&#10003;</span> {f}</span>))}
                </div>
                <div className="ey-ro-foot">AI-assisted check. Your reviewer confirms every result.</div>
              </div>
            </div>
          </div>
        </section>

        <div className="ey-strip">
          <div className="ey-wrap ey-strip-in">
            <span>POPIA-aligned</span><span>Your own AI keys</span><span>Encrypted at rest</span><span>Built for South African debit orders</span>
          </div>
        </div>

        <section className="ey-section ey-what">
          <div className="ey-wrap ey-what-grid">
            <div>
              <span className="ey-eyebrow">What Eyez is</span>
              <h2 className="ey-h2">A check on a mandate you already hold.</h2>
              <p className="ey-lead">Your customer authorises the debit order in your own channel. The mandate is signed, and usually already actioned, before it reaches Eyez.</p>
              <p className="ey-p">Eyez runs afterwards as a quality-assurance check on that existing mandate. It does not create, authorise, or action any debit, and your reviewer sees every result. The original signed mandate stays with you, as your record.</p>
            </div>
            <div className="ey-flow">
              <div className="ey-flow-step">
                <span className="ey-flow-ic"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21h14a2 2 0 0 0 2-2V9l-6-6H7a2 2 0 0 0-2 2z" /><path d="M14 3v6h6" /></svg></span>
                <span><b>Signed in your channel</b><small>Your onboarding, DebiCheck, or paper</small></span>
              </div>
              <div className="ey-flow-step">
                <span className="ey-flow-ic"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></svg></span>
                <span><b>Uploaded to Eyez</b><small>Voice or PDF, one or many</small></span>
              </div>
              <div className="ey-flow-step">
                <span className="ey-flow-ic"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3 8-8" /><path d="M21 12a9 9 0 1 1-6.2-8.6" /></svg></span>
                <span><b>QA report you review</b><small>Verdict, score, flags, transcript</small></span>
              </div>
              <p className="ey-flow-note">Eyez acts as your Operator. You remain the Responsible Party for the mandate.</p>
            </div>
          </div>
        </section>

        <section className="ey-section ey-steps" id="how">
          <div className="ey-wrap">
            <span className="ey-eyebrow">How it works</span>
            <h2 className="ey-h2">Three steps from a signed mandate to a report.</h2>
            <div className="ey-steps-row">
              <div className="ey-step"><div className="no">01 / Upload</div><h3>Send the mandate</h3><p>Upload a signed mandate as a voice recording or a PDF. Files with several mandates are detected and checked one by one.</p></div>
              <div className="ey-step"><div className="no">02 / Check</div><h3>Eyez reads it</h3><p>Eyez extracts the fields and checks them, and the wording, against the ruleset you choose. The AI runs on your own keys.</p></div>
              <div className="ey-step"><div className="no">03 / Review</div><h3>You get a report</h3><p>A clear verdict, a score, a field-by-field checklist, flags, and the full transcript. Your reviewer makes the call.</p></div>
            </div>
          </div>
        </section>

        <section className="ey-section ey-features">
          <div className="ey-wrap">
            <span className="ey-eyebrow">What you get</span>
            <h2 className="ey-h2">Everything you need to QA a mandate.</h2>
            <div className="ey-feat-grid">
              {features.map((f) => (
                <div className="ey-feat" key={f.t}>
                  <div className="ey-feat-ic">{f.i}</div>
                  <h3>{f.t}</h3>
                  <p>{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="ey-section ey-secure" id="security">
          <div className="ey-wrap">
            <span className="ey-eyebrow ey-eyebrow-cyan">Security and compliance</span>
            <h2 className="ey-h2">Built for compliance teams.</h2>
            <p className="ey-lead">The questions your risk and legal teams will ask, answered in the product.</p>
            <div className="ey-sec-grid">
              {security.map(([t, d]) => (
                <div className="ey-sec" key={t}><span className="ey-sec-ic"><i /></span><h3>{t}</h3><p>{d}</p></div>
              ))}
            </div>
          </div>
        </section>

        <section className="ey-section ey-audience">
          <div className="ey-wrap">
            <span className="ey-eyebrow">Who it is for</span>
            <h2 className="ey-h2">For teams that live with debit-order compliance.</h2>
            <div className="ey-chips">
              <span className="ey-chip">Payment service providers</span><span className="ey-chip">Banks</span><span className="ey-chip">Lenders</span><span className="ey-chip">Debit-order originators</span><span className="ey-chip">Collections teams</span>
            </div>
          </div>
        </section>

        <section className="ey-section ey-pricing" id="pricing">
          <div className="ey-wrap">
            <span className="ey-eyebrow ey-center">Pricing</span>
            <h2 className="ey-h2 ey-center">Simple monthly plans. 14-day free trial.</h2>
            <div className="ey-price-grid">
              {PLANS.map((p) => (
                <div className={'ey-plan' + (p.featured ? ' is-featured' : '')} key={p.id}>
                  {p.featured && <span className="ey-tag ey-popular">Most popular</span>}
                  <span className="ey-tag">{p.name}</span>
                  <h3 className="ey-price">{fmtR(p.monthly)}<span> / month</span></h3>
                  <p className="ey-who">{p.tagline}</p>
                  <ul className="ey-plan-ul">{p.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>
                  <button className={'ey-btn ' + (p.featured ? 'ey-btn-primary' : 'ey-btn-ghost')} onClick={() => onStart(p.id)}>Start free trial</button>
                </div>
              ))}
            </div>
            <p className="ey-price-note">Prices exclude VAT. You bring your own AI keys and pay those providers directly.</p>
          </div>
        </section>

        <section className="ey-section ey-faq" id="faq">
          <div className="ey-wrap">
            <span className="ey-eyebrow ey-center">FAQ</span>
            <h2 className="ey-h2 ey-center">Questions, answered.</h2>
            <div className="ey-faq-list">
              {faqs.map(([q, a]) => (
                <details className="ey-qa" key={q}>
                  <summary>{q}<span className="pl">+</span></summary>
                  <div className="ey-qa-ans">{a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="ey-section ey-ctaband">
          <div className="ey-wrap">
            <h2 className="ey-h2">Put an eye on every mandate.</h2>
            <p>Start a 14-day free trial, or sign in to your account.</p>
            <div className="ey-cta-row">
              <button className="ey-btn ey-btn-primary ey-btn-lg" onClick={() => onStart('growth')}>Start free trial</button>
              <a className="ey-btn ey-btn-ghost ey-btn-lg" href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer">Book a demo</a>
              <button className="ey-btn ey-btn-ghost ey-btn-lg" onClick={signIn}>Sign in</button>
            </div>
          </div>
        </section>
      </main>

      <footer className="ey-foot">
        <div className="ey-wrap">
          <div className="ey-foot-top">
            <div>
              <span className="ey-foot-logo"><IrisMark size={30} /><span className="ey-word">eyez</span></span>
              <p className="ey-foot-blurb">After-the-fact quality assurance for signed debit-order mandates.</p>
            </div>
            <div>
              <h4>Product</h4>
              <ul><li><a href="#how">How it works</a></li><li><a href="#security">Security</a></li><li><a href="#pricing">Pricing</a></li><li><a href="/login">Sign in</a></li></ul>
            </div>
            <div>
              <h4>Company</h4>
              <ul><li><a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer">Contact</a></li><li><a href="#how">About</a></li></ul>
            </div>
            <div>
              <h4>Legal</h4>
              <ul><li><a href="/legal/privacy-notice.pdf" target="_blank" rel="noopener noreferrer">Privacy</a></li><li><a href="/legal/master-service-agreement.pdf" target="_blank" rel="noopener noreferrer">Terms</a></li><li><a href="/legal/privacy-notice.pdf" target="_blank" rel="noopener noreferrer">Data requests</a></li></ul>
            </div>
          </div>
          <p className="ey-foot-disc">Eyez provides AI-assisted quality-assurance checks, not legal advice. Your team reviews every result, and you remain responsible for any decision made on a mandate.</p>
          {/* replace "Eyez" below with your registered entity name once CIPC registration completes */}
          <div className="ey-foot-bottom">
            <span>&copy; 2026 Eyez. All rights reserved.</span>
            <span className="tag">eyez ON YOU</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// -------------------------------------------------------------
// ONBOARDING
// -------------------------------------------------------------
function Onboard({ plan, initial, onBack, onContinue }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!data.company_name.trim()) e.company_name = 'Required';
      if (!data.industry) e.industry = 'Select one';
      if (!data.company_size) e.company_size = 'Select one';
    }
    if (step === 1) {
      if (!data.full_name.trim()) e.full_name = 'Required';
      if (!data.work_email.trim() || !/^\S+@\S+\.\S+$/.test(data.work_email)) e.work_email = 'Valid work email required';
      if (!data.phone.trim()) e.phone = 'Required';
      const pwError = checkPasswordStrength(data.password);
      if (pwError) e.password = pwError;
      if (!data.accept_terms) e.accept_terms = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = async () => {
    if (!validateStep()) return;
    setServerError(null);
    if (step < 1) {
      setStep(step + 1);
      return;
    }
    // Final step: create the company + user + trial on the server.
    setSubmitting(true);
    try {
      const session = await api('/signup/create', {
        method: 'POST',
        body: {
          company_name: data.company_name,
          industry: data.industry,
          company_size: data.company_size,
          plan_id: plan.id,
          full_name: data.full_name,
          work_email: data.work_email,
          phone: data.phone,
          password: data.password,
          accept_terms: data.accept_terms,
        },
      });
      onContinue(data, session);
    } catch (e) {
      // Common cases: 400 duplicate email/company → surface to the relevant field
      const msg = e.message || 'Sign-up failed. Please try again.';
      if (/email/i.test(msg)) setErrors((prev) => ({ ...prev, work_email: msg }));
      else if (/company/i.test(msg)) setErrors((prev) => ({ ...prev, company_name: msg }));
      else setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardShell onHome={onBack}>
      <OnboardHeader
        plan={plan}
        current={step}
        steps={['Company', 'Account']}
        onBack={step === 0 ? onBack : () => setStep(step - 1)}
        backLabel={step === 0 ? '← Back to plans' : '← Back'}
      />

      <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: '1fr 380px', gap: 60 }}>
        <div>
          {step === 0 && (
            <StepFrame
              title="Tell us about your company."
              sub="We need a bit of context to set up your tenant. Three quick fields, then your account."
            >
              <Field
                label="Company name"
                error={errors.company_name}
              >
                <input
                  type="text"
                  value={data.company_name}
                  onChange={(e) => update({ company_name: e.target.value })}
                  placeholder="e.g. Your Company (Pty) Ltd"
                  style={inputStyle}
                  autoFocus
                />
              </Field>

              <Field label="Industry" error={errors.industry}>
                <SelectGroup
                  value={data.industry}
                  onChange={(v) => update({ industry: v })}
                  options={[
                    { v: 'collections', l: 'Debt collection' },
                    { v: 'payments', l: 'Payments / TPPP' },
                    { v: 'lending', l: 'Lending / credit' },
                    { v: 'insurance', l: 'Insurance' },
                    { v: 'other', l: 'Other' },
                  ]}
                />
              </Field>

              <Field label="Team size" error={errors.company_size}>
                <SelectGroup
                  value={data.company_size}
                  onChange={(v) => update({ company_size: v })}
                  options={[
                    { v: '1-5', l: '1-5' },
                    { v: '6-20', l: '6-20' },
                    { v: '21-100', l: '21-100' },
                    { v: '100+', l: '100+' },
                  ]}
                />
              </Field>
            </StepFrame>
          )}

          {step === 1 && (
            <StepFrame
              title="Your admin account."
              sub="You'll be the first super-admin. You can invite the rest of your team once you're in."
            >
              <Field label="Full name" error={errors.full_name}>
                <input
                  type="text"
                  value={data.full_name}
                  onChange={(e) => update({ full_name: e.target.value })}
                  placeholder="Jane Doe"
                  style={inputStyle}
                  autoFocus
                />
              </Field>

              <Field label="Work email" error={errors.work_email}>
                <input
                  type="email"
                  value={data.work_email}
                  onChange={(e) => update({ work_email: e.target.value })}
                  placeholder="jane@yourcompany.co.za"
                  style={inputStyle}
                />
              </Field>

              <Field label="Mobile" error={errors.phone}>
                <input
                  type="tel"
                  value={data.phone}
                  onChange={(e) => update({ phone: e.target.value })}
                  placeholder="+27 82 123 4567"
                  style={inputStyle}
                />
              </Field>

              <Field label="Password" error={errors.password} hint="At least 10 characters, with a mix of letters, numbers, and symbols. You can enable MFA after sign-in.">
                <input
                  type="password"
                  value={data.password}
                  onChange={(e) => update({ password: e.target.value })}
                  placeholder="••••••••"
                  style={inputStyle}
                />
              </Field>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '12px 14px',
                  border: `1px solid ${errors.accept_terms ? '#DC2626' : BRAND.line}`,
                  borderRadius: 12,
                  cursor: 'pointer',
                  background: data.accept_terms ? '#F4FAFE' : '#fff',
                  marginTop: 8,
                }}
              >
                <input
                  type="checkbox"
                  checked={data.accept_terms}
                  onChange={(e) => update({ accept_terms: e.target.checked })}
                  style={{ marginTop: 3, width: 16, height: 16, accentColor: BRAND.cyanDeep }}
                />
                <span style={{ fontSize: 13, lineHeight: 1.5, color: BRAND.inkSoft }}>
                  I agree to the <a href="/legal/master-service-agreement.pdf" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.cyanDeep, fontWeight: 500 }}>Master Service Agreement</a> and acknowledge the <a href="/legal/popia-operator-agreement.pdf" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.cyanDeep, fontWeight: 500 }}>POPIA Operator clauses</a>. I confirm I have authority to sign on behalf of my company.
                </span>
              </label>
              {errors.accept_terms && <FieldError msg={errors.accept_terms} />}
            </StepFrame>
          )}

          {serverError && (
            <div style={{
              marginTop: 24, padding: '12px 14px',
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 10, color: '#DC2626', fontSize: 13,
              fontFamily: '"JetBrains Mono", monospace',
            }}>
              → {serverError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 36 }}>
            <button
              onClick={next}
              disabled={submitting}
              style={{
                background: submitting ? BRAND.muted : BRAND.ink,
                color: '#fff',
                border: 'none',
                padding: '15px 26px',
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                cursor: submitting ? 'wait' : 'pointer',
                fontFamily: '"JetBrains Mono", monospace',
                letterSpacing: '0.04em',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              {submitting ? (<><Spinner /> CREATING ACCOUNT…</>) : (<>CONTINUE →</>)}
            </button>
          </div>
        </div>

        <OrderSummary plan={plan} />
      </div>
    </OnboardShell>
  );
}

function OnboardShell({ children, onHome }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '24px 28px', borderBottom: `1px solid ${BRAND.line}`, background: '#fff' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }}>
          {onHome ? (
            <button type="button" onClick={onHome} aria-label="Eyez home" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <Wordmark size={32} />
            </button>
          ) : (
            <Wordmark size={32} />
          )}
        </div>
      </div>
      <div style={{ flex: 1, padding: '60px 28px 120px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function OnboardHeader({ plan, current, steps, onBack, backLabel }) {
  return (
    <>
      <button
        onClick={onBack}
        style={{
          background: 'none',
          border: 'none',
          color: BRAND.muted,
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 11,
          letterSpacing: '0.08em',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 24,
        }}
      >
        {backLabel}
      </button>
      <Stepper current={current} steps={steps} />
    </>
  );
}

function StepFrame({ title, sub, children }) {
  return (
    <>
      <h1 style={{
        fontFamily: '"Fraunces", serif',
        fontSize: 44,
        fontWeight: 500,
        letterSpacing: '-0.025em',
        margin: 0,
        lineHeight: 1.05,
        color: BRAND.ink,
      }}>
        {title}
      </h1>
      <p style={{ fontSize: 15, color: BRAND.inkSoft, lineHeight: 1.6, marginTop: 12, marginBottom: 36, maxWidth: 520 }}>
        {sub}
      </p>
      <div style={{ display: 'grid', gap: 20, maxWidth: 540 }}>
        {children}
      </div>
    </>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 10,
        letterSpacing: '0.14em',
        color: BRAND.muted,
        marginBottom: 8,
        textTransform: 'uppercase',
      }}>
        {label}
      </label>
      {children}
      {hint && !error && <div style={{ fontSize: 11.5, color: BRAND.muted, marginTop: 6 }}>{hint}</div>}
      {error && <FieldError msg={error} />}
    </div>
  );
}

function FieldError({ msg }) {
  return (
    <div style={{
      fontSize: 11.5,
      color: '#DC2626',
      marginTop: 6,
      fontFamily: '"JetBrains Mono", monospace',
      letterSpacing: '0.04em',
    }}>
      → {msg}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '13px 16px',
  fontSize: 15,
  fontFamily: 'inherit',
  color: BRAND.ink,
  background: '#fff',
  border: `1px solid ${BRAND.line}`,
  borderRadius: 12,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color .2s ease, box-shadow .2s ease',
};

function SelectGroup({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            style={{
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'inherit',
              background: active ? BRAND.ink : '#fff',
              color: active ? '#fff' : BRAND.inkSoft,
              border: `1px solid ${active ? BRAND.ink : BRAND.line}`,
              borderRadius: 999,
              cursor: 'pointer',
              transition: 'all .15s ease',
            }}
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

function OrderSummary({ plan, includesTrial = true }) {
  if (!plan) return null;
  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${BRAND.line}`,
      borderRadius: 18,
      padding: 24,
      position: 'sticky',
      top: 100,
      alignSelf: 'start',
    }}>
      <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.16em', color: BRAND.cyan, marginBottom: 12 }}>
        · YOUR ORDER ·
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontFamily: '"Fraunces", serif', fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em' }}>
          {plan.name}
        </span>
        <span style={{ fontSize: 13, color: BRAND.muted }}>monthly</span>
      </div>
      <div style={{ fontSize: 13, color: BRAND.inkSoft, marginBottom: 24, lineHeight: 1.5 }}>
        {plan.tagline}
      </div>

      <div style={{ borderTop: `1px dashed ${BRAND.line}`, paddingTop: 16 }}>
        <SummaryRow label="Subscription" value={`${fmtR(plan.monthly)}/mo`} />
        <SummaryRow label="Per voice mandate" value={`R${plan.perVoice.toFixed(2)}`} />
        <SummaryRow label="Per PDF mandate" value={`R${plan.perPdf.toFixed(2)}`} />
        <SummaryRow label="Seats" value={plan.seats} />
      </div>

      {includesTrial && (
        <div style={{
          marginTop: 20,
          padding: '14px 16px',
          background: '#F4FAFE',
          border: `1px solid #CCE9F5`,
          borderRadius: 12,
          fontSize: 12.5,
          lineHeight: 1.5,
          color: BRAND.inkSoft,
        }}>
          <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: BRAND.cyanDeep, marginBottom: 4 }}>
            14-DAY TRIAL
          </div>
          We won't charge your card during the trial. Your first charge is when the 14-day trial ends. Cancel any time from the admin panel.
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0' }}>
      <span style={{ fontSize: 12.5, color: BRAND.muted, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.04em' }}>{label}</span>
      <span style={{ fontSize: 13, color: BRAND.ink, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

// -------------------------------------------------------------
// PAYMENT
// -------------------------------------------------------------
function Payment({ plan, onboard, session, onBack, onComplete }) {
  const [card, setCard] = useState({
    number: '',
    name: '',
    expiry: '',
    cvc: '',
    save: true,
  });
  const [billing, setBilling] = useState({
    company: onboard.company_name,
    vat: '',
    address: '',
    city: '',
    postcode: '',
    country: 'South Africa',
  });
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);

  const formatCard = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 19);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  const formatExp = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    if (digits.length < 3) return digits;
    return digits.slice(0, 2) + ' / ' + digits.slice(2);
  };

  const detectBrand = (num) => {
    const n = num.replace(/\s/g, '');
    if (n.startsWith('4')) return 'VISA';
    if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) return 'MASTERCARD';
    if (/^3[47]/.test(n)) return 'AMEX';
    return null;
  };
  const brand = detectBrand(card.number);

  const submit = async () => {
    setServerError(null);
    const e = {};
    const digits = card.number.replace(/\s/g, '');
    if (digits.length < 13) e.number = 'Enter a valid card number';
    if (!card.name.trim()) e.name = 'Cardholder name required';
    if (!/^\d{2}\s\/\s\d{2}$/.test(card.expiry)) e.expiry = 'MM / YY';
    if (card.cvc.length < 3) e.cvc = 'Min 3 digits';
    if (!billing.address.trim()) e.address = 'Required';
    if (!billing.city.trim()) e.city = 'Required';
    if (!billing.postcode.trim()) e.postcode = 'Required';
    setErrors(e);
    if (Object.keys(e).length) return;

    if (!session || !session.access_token) {
      setServerError('Your signup session was lost. Please go back and try again.');
      return;
    }

    const [mm, yy] = card.expiry.split(' / ');

    setProcessing(true);
    try {
      const result = await api('/signup/payment', {
        method: 'POST',
        token: session.access_token,
        body: {
          pan: digits,
          cardholder_name: card.name,
          expiry_mm: mm,
          expiry_yy: yy,
          cvc: card.cvc,
          billing_company: billing.company || null,
          billing_vat: billing.vat || null,
          billing_address: billing.address,
          billing_city: billing.city,
          billing_postcode: billing.postcode,
          billing_country: billing.country,
        },
      });

      if (!result.success) {
        // Processor declined: show specific reason, do not progress.
        setServerError(result.error_message || 'Card was declined. Try another card.');
        setProcessing(false);
        return;
      }
      // Success: move to welcome screen.
      onComplete();
    } catch (err) {
      setServerError(err.message || 'Payment could not be processed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <OnboardShell>
      <OnboardHeader
        plan={plan}
        current={2}
        steps={['Company', 'Account', 'Payment']}
        onBack={onBack}
        backLabel="← Back to account"
      />

      <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: '1fr 380px', gap: 60 }}>
        <div>
          <h1 style={{
            fontFamily: '"Fraunces", serif',
            fontSize: 44,
            fontWeight: 500,
            letterSpacing: '-0.025em',
            margin: 0,
            lineHeight: 1.05,
            color: BRAND.ink,
          }}>
            Card on file.
          </h1>
          <p style={{ fontSize: 15, color: BRAND.inkSoft, lineHeight: 1.6, marginTop: 12, marginBottom: 36, maxWidth: 520 }}>
            Adding a card is optional. We store it securely with Peach Payments and charge nothing during your 14-day trial. Your first charge is when the trial ends, then monthly after that.
          </p>

          {/* card visual */}
          <CardVisual card={card} brand={brand} />

          <div style={{ marginTop: 28, display: 'grid', gap: 18, maxWidth: 540 }}>
            <Field label="Card number" error={errors.number}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={card.number}
                  onChange={(e) => setCard({ ...card, number: formatCard(e.target.value) })}
                  placeholder="4242 4242 4242 4242"
                  style={{ ...inputStyle, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.04em' }}
                  inputMode="numeric"
                  autoComplete="cc-number"
                />
                {brand && (
                  <span style={{
                    position: 'absolute',
                    right: 14, top: '50%',
                    transform: 'translateY(-50%)',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.14em',
                    padding: '4px 8px',
                    background: BRAND.ink,
                    color: '#fff',
                    borderRadius: 4,
                  }}>{brand}</span>
                )}
              </div>
            </Field>

            <Field label="Cardholder name" error={errors.name}>
              <input
                type="text"
                value={card.name}
                onChange={(e) => setCard({ ...card, name: e.target.value.toUpperCase() })}
                placeholder="AS ON CARD"
                style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                autoComplete="cc-name"
              />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Expiry" error={errors.expiry}>
                <input
                  type="text"
                  value={card.expiry}
                  onChange={(e) => setCard({ ...card, expiry: formatExp(e.target.value) })}
                  placeholder="MM / YY"
                  style={{ ...inputStyle, fontFamily: '"JetBrains Mono", monospace' }}
                  inputMode="numeric"
                  autoComplete="cc-exp"
                />
              </Field>
              <Field label="CVC" error={errors.cvc}>
                <input
                  type="password"
                  value={card.cvc}
                  onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="•••"
                  style={{ ...inputStyle, fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.2em' }}
                  inputMode="numeric"
                  autoComplete="cc-csc"
                />
              </Field>
            </div>
          </div>

          <h2 style={{
            fontFamily: '"Fraunces", serif',
            fontSize: 28,
            fontWeight: 500,
            letterSpacing: '-0.02em',
            marginTop: 48,
            marginBottom: 20,
            color: BRAND.ink,
          }}>
            Billing address.
          </h2>

          <div style={{ display: 'grid', gap: 16, maxWidth: 540 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Company name">
                <input
                  type="text"
                  value={billing.company}
                  onChange={(e) => setBilling({ ...billing, company: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <Field label="VAT number (optional)">
                <input
                  type="text"
                  value={billing.vat}
                  onChange={(e) => setBilling({ ...billing, vat: e.target.value })}
                  placeholder="4123456789"
                  style={inputStyle}
                />
              </Field>
            </div>
            <Field label="Street address" error={errors.address}>
              <input
                type="text"
                value={billing.address}
                onChange={(e) => setBilling({ ...billing, address: e.target.value })}
                placeholder="1 Long Street"
                style={inputStyle}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
              <Field label="City" error={errors.city}>
                <input
                  type="text"
                  value={billing.city}
                  onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                  placeholder="Cape Town"
                  style={inputStyle}
                />
              </Field>
              <Field label="Postcode" error={errors.postcode}>
                <input
                  type="text"
                  value={billing.postcode}
                  onChange={(e) => setBilling({ ...billing, postcode: e.target.value })}
                  placeholder="8001"
                  style={inputStyle}
                />
              </Field>
            </div>
          </div>

          <div style={{
            marginTop: 32,
            padding: '14px 16px',
            background: '#F8FAFC',
            border: `1px solid ${BRAND.line}`,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 12.5,
            color: BRAND.muted,
          }}>
            <LockIcon />
            Card data is tokenised by Peach Payments, a PCI-DSS Level 1 processor. eyez never sees the PAN or CVC.
          </div>

          {serverError && (
            <div style={{
              marginTop: 24, padding: '12px 14px',
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 10, color: '#DC2626', fontSize: 13,
              fontFamily: '"JetBrains Mono", monospace',
            }}>
              → {serverError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 32 }}>
            <button
              onClick={submit}
              disabled={processing}
              style={{
                background: processing ? BRAND.muted : BRAND.ink,
                color: '#fff',
                border: 'none',
                padding: '15px 26px',
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                cursor: processing ? 'wait' : 'pointer',
                fontFamily: '"JetBrains Mono", monospace',
                letterSpacing: '0.04em',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              {processing ? (
                <>
                  <Spinner />
                  SAVING…
                </>
              ) : (
                <>
                  SAVE CARD & START TRIAL
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: BRAND.cyan }} />
                </>
              )}
            </button>
            <button
              onClick={onComplete}
              disabled={processing}
              style={{
                background: 'transparent',
                color: BRAND.ink,
                border: `1px solid ${BRAND.line}`,
                padding: '15px 22px',
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                cursor: processing ? 'wait' : 'pointer',
                fontFamily: '"JetBrains Mono", monospace',
                letterSpacing: '0.04em',
              }}
            >
              SKIP, START TRIAL
            </button>
          </div>
          <p style={{ marginTop: 12, fontSize: 13, color: BRAND.muted }}>
            You can add a card now or skip it. Your 14-day trial starts either way. We only ask for payment when the trial ends.
          </p>
        </div>

        <OrderSummary plan={plan} />
      </div>
    </OnboardShell>
  );
}

function CardVisual({ card, brand }) {
  return (
    <div style={{
      width: '100%',
      maxWidth: 380,
      aspectRatio: '1.586 / 1',
      borderRadius: 18,
      padding: 24,
      background: `linear-gradient(135deg, ${BRAND.ink} 0%, #1A2230 60%, ${BRAND.cyanDeep} 130%)`,
      color: '#fff',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 30px 60px -25px rgba(11,15,20,0.45)',
      fontFamily: '"JetBrains Mono", monospace',
    }}>
      <div style={{ position: 'absolute', top: -40, right: -40, opacity: 0.18 }}>
        <IrisMark size={220} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IrisMark size={28} />
          <span style={{ fontFamily: '"Fraunces", serif', fontSize: 18, fontWeight: 500, letterSpacing: '-0.01em' }}>eyez</span>
        </div>
        <span style={{ fontSize: 11, letterSpacing: '0.14em', color: BRAND.cyan }}>
          {brand || 'CARD'}
        </span>
      </div>
      <div style={{ position: 'absolute', bottom: 24, left: 24, right: 24 }}>
        <div style={{ fontSize: 18, letterSpacing: '0.12em', marginBottom: 16, minHeight: 22 }}>
          {card.number || '•••• •••• •••• ••••'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9CA3AF' }}>
          <div>
            <div style={{ marginBottom: 4 }}>Cardholder</div>
            <div style={{ color: '#fff', fontSize: 12, letterSpacing: '0.05em' }}>{card.name || 'YOUR NAME'}</div>
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>Expires</div>
            <div style={{ color: '#fff', fontSize: 12 }}>{card.expiry || 'MM / YY'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BRAND.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function Spinner() {
  return (
    <span style={{
      width: 12, height: 12, borderRadius: '50%',
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: '#fff',
      display: 'inline-block',
      animation: 'spin 0.8s linear infinite',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

// -------------------------------------------------------------
// DONE / WELCOME
// -------------------------------------------------------------
function Done({ onboard, plan, onHome }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '24px 28px', borderBottom: `1px solid ${BRAND.line}`, background: '#fff' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto' }}>
          {onHome ? (
            <button type="button" onClick={onHome} aria-label="Eyez home" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
              <Wordmark size={32} />
            </button>
          ) : (
            <Wordmark size={32} />
          )}
        </div>
      </div>

      <div style={{ flex: 1, padding: '80px 28px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', opacity: 0.05, pointerEvents: 'none' }}>
          <IrisMark size={600} animate />
        </div>

        <div style={{ maxWidth: 680, margin: '0 auto', position: 'relative', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', marginBottom: 32 }}>
            <IrisMark size={88} animate />
          </div>
          <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: '0.2em', color: BRAND.cyan, marginBottom: 16 }}>
            · AWAITING ACTIVATION ·
          </div>
          <h1 style={{
            fontFamily: '"Fraunces", serif',
            fontSize: 64,
            fontWeight: 500,
            letterSpacing: '-0.03em',
            margin: 0,
            lineHeight: 1.02,
            color: BRAND.ink,
          }}>
            Almost there, {onboard.full_name.split(' ')[0] || 'there'}.
          </h1>
          <p style={{ fontSize: 17, color: BRAND.inkSoft, lineHeight: 1.6, marginTop: 24, marginBottom: 0 }}>
            Your <strong>{plan?.name}</strong> workspace for <strong>{onboard.company_name}</strong> has been created and is awaiting activation by our team. Your <strong>14-day trial</strong> starts the moment it is activated, and we'll let you know at <strong style={{ color: BRAND.cyanDeep }}>{onboard.work_email}</strong>.
          </p>

          <div style={{
            marginTop: 40,
            background: '#fff',
            border: `1px solid ${BRAND.line}`,
            borderRadius: 18,
            padding: 28,
            textAlign: 'left',
          }}>
            <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: '0.16em', color: BRAND.muted, marginBottom: 14 }}>
              · ONCE YOU'RE ACTIVATED ·
            </div>
            <NextStep n="1" t="Sign in & verify your email" d="We've emailed you a verification link. Click it to confirm and unlock all features." />
            <NextStep n="2" t="Add your OpenAI & Anthropic keys" d="Go to Account → API Keys. You'll need both before you can run verifications." />
            <NextStep n="3" t="Invite your team" d="Users → Invite. Roles: company admin or verifier." />
            <NextStep n="4" t="Run your first verification" d="Upload a voice recording or PDF mandate. You'll get a verdict in seconds." last />
          </div>

          <div style={{ marginTop: 36, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.open(WHATSAPP_HREF, '_blank', 'noopener,noreferrer')}
              style={{
                background: BRAND.ink,
                color: '#fff',
                border: 'none',
                padding: '15px 28px',
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: '"JetBrains Mono", monospace',
                letterSpacing: '0.04em',
              }}
            >
              MESSAGE US ON WHATSAPP
            </button>
            <button
              onClick={() => onHome && onHome()}
              style={{
                background: 'transparent',
                color: BRAND.ink,
                border: `1px solid ${BRAND.ink}`,
                padding: '14px 26px',
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: '"JetBrains Mono", monospace',
                letterSpacing: '0.04em',
              }}
            >
              BACK TO HOME
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NextStep({ n, t, d, last }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '32px 1fr',
      gap: 18,
      padding: '16px 0',
      borderBottom: last ? 'none' : `1px solid ${BRAND.line}`,
    }}>
      <div style={{
        width: 28, height: 28,
        borderRadius: '50%',
        background: BRAND.ink,
        color: BRAND.cyan,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 12,
        fontWeight: 700,
      }}>{n}</div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: BRAND.ink, marginBottom: 4 }}>{t}</div>
        <div style={{ fontSize: 13.5, color: BRAND.inkSoft, lineHeight: 1.5 }}>{d}</div>
      </div>
    </div>
  );
}
